import { useLocation, useNavigate, useParams } from "react-router-dom";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { useEffect, useState } from "react";
import {
  fetchRoleById,
  getAllClaimGroupList,
  handleSubmitRole,
  IRolesForm,
  roleFieldValidationSchema,
  ROLES_FIELD_INITIAL_VALUES,
} from "./RoleFormController";
import { IClaims } from "../../data/AppInterface";
import { routeAdmin, routeMaster } from "../../routes/AuthRoutePath";
import { MESSAGE_UNKNOWN_ERROR_OCCURRED } from "../../utils/AppConstants";
import {
  getLocalDate,
  prepareMessageFromParams,
  sweetAlertError,
} from "../../utils/AppFunctions";
import Loader from "../../core-component/ui/Loader";
import { AdvanceSearch, ICButton } from "../../core-component/ui";
import { Form, Formik } from "formik";
import { ICFormikSwitch, ICFormikTextInput } from "../../core-component/formik";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  IClaimGroup,
  IClaimOtherGroup,
} from "../claim-group/ClaimGroupController";
import { ICCheckbox } from "../../core-component/form";
import { cn } from "../../lib";

interface IRoleFormProps {
  handleClose?: (status: boolean) => void;
}

const SingleClaimGroup = ({
  showSide,
  groupList,
  selectedClaims,
  onToggleGroup,
  onToggleClaim,
  readOnly,
}: {
  showSide?: boolean;
  groupList: IClaimGroup[];
  selectedClaims: IClaims[];
  onToggleGroup: (claims: IClaims[]) => void;
  onToggleClaim: (id: string, claim: IClaims[]) => void;
  readOnly: boolean;
}) => {
  return (
    <>
      {groupList.map((group) => {
        const isGroupSelected = group.claims.every((claim) =>
          selectedClaims.some((selected) => selected.id === claim.id),
        );

        return (
          <div
            key={group.id}
            className={cn("bg-subtle p-4 rounded-md", {
              "lg:col-span-2": showSide,
            })}
          >
            <div className="bg-accent-muted rounded-t-md border-b-2 border-muted">
              <div className="flex items-center px-2 py-1">
                <div className="text-invert flex-shrink-0 mr-3">
                  <ICCheckbox
                    checkboxClassName="size-3.5"
                    checked={isGroupSelected}
                    onChange={() => onToggleGroup(group.claims)}
                    disabled={readOnly}
                  />
                </div>
                <h2 className="text-lg font-medium">
                  {group.claim_group_name}
                </h2>
              </div>
            </div>
            <div
              className={cn("grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 pl-2", {
                "sm:grid-cols-4": showSide,
              })}
            >
              {group.claims.map((claim) => (
                <div key={claim.id} className="flex items-start">
                  <div className="text-invert flex-shrink-0 mr-3">
                    <ICCheckbox
                      checkboxClassName="size-3.5 mt-1"
                      checked={selectedClaims.some(
                        (selected) => selected.id === claim.id,
                      )}
                      onChange={() => onToggleClaim(claim.id, group.claims)}
                      disabled={readOnly}
                    />
                  </div>
                  <div className="leading-tight">{claim.claim_name}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
};
const RoleForm: React.FC<IRoleFormProps> = ({ handleClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: roleId } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const isEdit = queryParams.get("isEdit") === "true";
  const readOnly = Boolean(roleId) && !isEdit;
  const backTo: string =
    location.state?.backTo ?? `/${routeMaster.admin}/${routeAdmin.roles}`;
  const goBack = () => navigate(backTo);
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [addClaims, setAddClaims] = useState<IClaims[]>([]);
  const [roleFormInitialValues, setRoleFormInitialValues] =
    useState<IRolesForm>(ROLES_FIELD_INITIAL_VALUES);
  const [claimGroupList, setClaimGroupList] = useState<IClaimGroup[]>([]);
  const [claimOtherGroupList, setClaimOtherGroupList] = useState<
    IClaimOtherGroup[]
  >([]);
  useEffect(() => {
    if (roleId) {
      getRoleById(roleId);
    } else {
      setRoleFormInitialValues(ROLES_FIELD_INITIAL_VALUES);
    }
  }, [roleId]);

  useEffect(() => {
    fetchClaimGroup();
  }, []);

  const getRoleById = async (id: string) => {
    setLoading(true);
    await fetchRoleById(
      id,
      handleClose ?? goBack,
      setRoleFormInitialValues,
      setAddClaims,
    );
    setLoading(false);
  };

  const handleSubmit = async (values: IRolesForm) => {
    if (addClaims.length === 0) {
      sweetAlertError({
        message: prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("claims")],
        ]),
      });
      return;
    }
    try {
      setSubmitLoading(true);
      await handleSubmitRole(
        {
          ...values,
          claims: addClaims.map((values) => values.id),
        },
        setSubmitLoading,
        roleId,
        handleClose ?? goBack,
      );
    } catch {
      sweetAlertError({ message: MESSAGE_UNKNOWN_ERROR_OCCURRED });
    } finally {
      setSubmitLoading(false);
    }
  };
  const fetchClaimGroup = async () => {
    setLoading(true);
    await getAllClaimGroupList(
      setClaimGroupList,
      setClaimOtherGroupList,
      BIT_VALUE.One,
    );
    setLoading(false);
  };
  const handleCheckboxChange = (
    prevClaims: IClaims[],
    claimId: string,
    itemClaims: IClaims[] = [],
  ) => {
    const isAlreadyAdded = prevClaims.some(
      (addedClaim) => addedClaim.id === claimId,
    );
    if (isAlreadyAdded) {
      return prevClaims.filter((addedClaim) => addedClaim.id !== claimId);
    } else {
      const claimsToAdd =
        itemClaims.length > 0
          ? itemClaims
              .filter((claim) => claim.id === claimId)
              .map((claim) => ({
                id: claim.id,
                claim_name: claim.claim_name,
              }))
          : [{ id: claimId, claim_name: "" }];
      return [...prevClaims, ...claimsToAdd];
    }
  };
  const checkAllClaims = (claimsToCheck: IClaims[], prevClaims: IClaims[]) => {
    const areAllSelected = claimsToCheck.every((claim) =>
      prevClaims.some((addedClaim) => addedClaim.id === claim.id),
    );

    if (areAllSelected) {
      return prevClaims.filter(
        (addedClaim) =>
          !claimsToCheck.some((claim) => claim.id === addedClaim.id),
      );
    } else {
      const uniqueClaimsToAdd = claimsToCheck.filter(
        (claim) => !prevClaims.some((addedClaim) => addedClaim.id === claim.id),
      );

      return [...prevClaims, ...uniqueClaimsToAdd];
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <div className="flex flex-col overflow-y-hidden flex-1 p-4">
          <AdvanceSearch
            backButton
            title={
              roleFormInitialValues?.role_name
                ? `${t("role_name")} (${roleFormInitialValues.role_name})`
                : t("roles_form")
            }
            createMassage={
              roleId
                ? {
                    name: roleFormInitialValues?.created_by ?? "",
                    date: getLocalDate(roleFormInitialValues?.created_date),
                  }
                : undefined
            }
            modifiedMassage={
              roleId && roleFormInitialValues?.modified_date
                ? {
                    name: roleFormInitialValues?.modified_by ?? "",
                    date: getLocalDate(roleFormInitialValues?.modified_date),
                  }
                : undefined
            }
          />
          {roleFormInitialValues && (
            <Formik
              initialValues={roleFormInitialValues}
              validationSchema={roleFieldValidationSchema(t)}
              onSubmit={(values) => handleSubmit(values)}
            >
              {(formikProps) => {
                const { handleSubmit } = formikProps;
                return (
                  <Form
                    onSubmit={handleSubmit}
                    className="flex flex-col h-full mt-4 overflow-y-hidden"
                  >
                    <div className="grid grid-cols-1 bg-subtle lg:grid-cols-2 gap-4 mb-4 p-4 rounded-md">
                      <ICFormikTextInput
                        {...formikProps}
                        fieldName="role_name"
                        placeholder={t("role_name")}
                        required
                        tabIndex={0}
                        disabled={readOnly}
                        autoFocus={true}
                      />
                      <ICFormikSwitch
                        {...formikProps}
                        name="is_active"
                        containerClassName="gap-4 !w-fit"
                        placeholder={t("status")}
                        checkedValue={BIT_VALUE.One}
                        unCheckedValue={BIT_VALUE.Zero}
                        disabled={readOnly}
                      />
                    </div>
                    <div className="flex-1 flex-col overflow-y-scroll mb-4 ">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 ">
                        <SingleClaimGroup
                          groupList={claimGroupList}
                          selectedClaims={addClaims}
                          onToggleGroup={(claimsToAdd) => {
                            setAddClaims((prevClaims) =>
                              checkAllClaims(claimsToAdd, prevClaims),
                            );
                          }}
                          onToggleClaim={(claimId, claimToAdd) => {
                            setAddClaims((prevClaims) =>
                              handleCheckboxChange(
                                prevClaims,
                                claimId,
                                claimToAdd,
                              ),
                            );
                          }}
                          readOnly={readOnly}
                        />
                        {claimOtherGroupList.length > 0 && (
                          <SingleClaimGroup
                            showSide={claimGroupList.length % 2 === 0}
                            groupList={[
                              {
                                // synthetic "other claims" pseudo-group — not a real DB group;
                                // id is React-key-only. 0-sentinel → nil-uuid (Task 1707, 08 §0).
                                id: "00000000-0000-0000-0000-000000000000",
                                claims: claimOtherGroupList,
                                is_active: BIT_VALUE.One,
                                is_deleted: BIT_VALUE.Zero,
                                claim_group_name: t("other_claims"),
                              },
                            ]}
                            selectedClaims={addClaims}
                            onToggleGroup={(claimsToAdd) => {
                              setAddClaims((prevClaims) =>
                                checkAllClaims(claimsToAdd, prevClaims),
                              );
                            }}
                            onToggleClaim={(claimId, claimToAdd) => {
                              setAddClaims((prevClaims) =>
                                handleCheckboxChange(
                                  prevClaims,
                                  claimId,
                                  claimToAdd,
                                ),
                              );
                            }}
                            readOnly={readOnly}
                          />
                        )}
                      </div>
                    </div>

                    <div>
                      {!readOnly && (
                        <ICButton
                          loading={submitLoading}
                          className="w-full"
                          children={t("submit")}
                          disabled={submitLoading}
                        />
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          )}
        </div>
      )}
    </>
  );
};
export default RoleForm;
